import { NetworkStatus } from '@cbridge-utils/network';
import { INode } from '@cbridge-packages/node';
import { IStream } from '@cbridge-packages/stream';
import { IClient, ITradingClientSettings, IPricingClientSettings } from '@cbridge-packages/client';
import { ApiAutoSync, IApiServerSettings, IApiTradingConnections } from '@cbridge-packages/api';

export interface IServerStreamSettings {
  tradingGroups?: string;
  securities?: string;
  symbols?: string;
  useSymbolsConjunction?: boolean;
  rejectClientRequestIfOffline?: boolean;
}

export interface IServerStream {
  serverId: IServer['serverId'];
  streamId: IStream['streamId'];
  streamName: string;
  tradingEnabled: boolean;
  pricingEnabled: boolean;
  clientSymbolsCount?: number;
  settings?: IServerStreamSettings;
}

export interface IServerStreamForm {
  serverId?: IServer['serverId'];
  streamId?: IStream['streamId'];
  tradingEnabled?: boolean;
  pricingEnabled?: boolean;
  settings?: IServerStreamSettings;
  json?: string;
}

export enum ServerType {
  MT4 = 'MT4',
  MT5 = 'MT5',
  cTrader = 'cTrader',
  FixTaker = 'FIX_TAKER',
}

export enum ConnectionStatus {
  Online = 'Online',
  Offline = 'Offline',
  Na = 'Na',
  Disabled = 'Disabled',
  Unknown = 'Unknown',
}

export interface IServerBase {
  serverId: string;
  serverName: string;
  nodeId: INode['nodeId'];
  nodeName: string;
  type: ServerType;
  tradingConnections?: IApiTradingConnections;
  /**
   * in milliseconds
   */
  throttlingInterval?: number;
  /**
   * Independent markout (risk reporting) on this server; details only, the list has no such field (LB-4701)
   */
  riskReportingEnabled?: boolean;
}

export interface IServerForm extends Omit<IServerBase, 'tradingConnections' | 'throttlingInterval'> {
  /**
   * in milliseconds; null = cleared in the form, the API request then omits the field
   */
  throttlingInterval?: number | null;
}

export interface IServer extends IServerBase {
  streamsCount?: number;
  clientSymbolCount?: number;

  // that field has a list of api response but hasn't in details response
  autosync?: ApiAutoSync;
  connectionStatus?: ConnectionStatus;
  pricingConnectionStatus?: ConnectionStatus;
}

export type IServerSettings = IApiServerSettings;

export interface IServerDetails extends IServer {
  pricingClient: IPricingClient;
  settings?: IServerSettings;
  defaultSettings?: {
    pricingSession: IPricingClientSettings;
    tradingSession: ITradingClientSettings;
  };
  tradingClients: ITradingClient[];
  streams: IServerStream[];
}

export interface IConnectedClient {
  clientId: IClient['clientId'];
  clientSymbolsCount: number;
  enabled: boolean;
  serverId: IServer['serverId'];
}

export interface IPricingClient extends IConnectedClient {
  settings?: IPricingClientSettings;
}

export interface IPricingClientForm {
  settings?: IPricingClientSettings;
  json?: string;
}

export interface ITradingClient extends IConnectedClient {
  nodeId: IServer['nodeId'];
  status?: ConnectionStatus;
}

export interface ITradingClientForm {
  clientId?: IClient['clientId'];
  serverId?: IServer['serverId'];
  streamId?: IStream['streamId'];
  enabled?: boolean;
  settings?: ITradingClientSettings;
  json?: string;
}

export interface IServerListKey {
  nodeId?: INode['nodeId'];
}

export interface IServerStoreData extends IServerDetails {
  networkStatus?: NetworkStatus;
}

export interface IServerStore {
  data: Record<IServer['serverId'], IServerStoreData>;
  listNetworkStatus: NetworkStatus;
}

export interface IServerStoreSegment {
  server: IServerStore;
}
